<template>
  <div class="bridge-signing-table">
    <v-card-title class="toolbar primary white--text">
      <div>
        <span class="headline">
          <h1 class="headline ml-2">
            <v-icon dark>description</v-icon>
            Securities
          </h1>
        </span>
      </div>
      <v-spacer />
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Filter results"
        single-line
        flat
        dark
        color="primary"
        solo-inverted
        hide-details
      />
      <v-tooltip
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            color="secondary"
            fab
            dark
            absolute
            bottom
            right
            @click.stop="onCreate"
            v-on="on"
          >
            <v-icon>note_add</v-icon>
          </v-btn>
        </template>
        <span>Create new security</span>
      </v-tooltip>

      <v-tooltip
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            class="mass-create"
            color="secondary"
            fab
            dark
            absolute
            bottom
            right
            @click.stop="showDialog"
            v-on="on"
          >
            <v-icon>file_upload</v-icon>
          </v-btn>
        </template>
        <span>Upload CSV</span>
      </v-tooltip>

      <v-tooltip
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            color="secondary"
            fab
            dark
            absolute
            bottom
            left
            @click.stop="onRefresh"
            v-on="on"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
        </template>
        <span>Refresh</span>
      </v-tooltip>
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
    >
      <template
        slot="headers"
        slot-scope="props"
      >
        <tr>
          <th
            v-for="header in props.headers"
            :key="header.text"
          >
            {{ header.textHeader }}
            <br>
            {{ header.text }}
          </th>
        </tr>
      </template>
      <template
        slot="items"
        slot-scope="props"
      >
        <tr>
          <td class="text-center">
            {{ props.item.id }}
          </td>
          <td class="text-center">
            {{ props.item.fp_id }}
          </td>
          <td class="text-center">
            {{ props.item.name }}
          </td>
          <td class="text-center">
            {{ types()[props.item.type] }}
          </td>
          <td class="text-center">
            {{ custodians()[props.item.custodian] }}
          </td>
          <td class="text-center">
            {{ props.item.notional }}
          </td>
          <td class="text-center">
            {{ props.item.taxable_in }}
          </td>
          <td class="text-xs-center">
            {{ props.item.regulation_type }}
          </td>
          <!-- Issuer -->
          <td class="text-center">
            <span
              v-if="props.item.issuer.company_id"
            >
              {{ props.item.issuer.company_id }}
            </span>
            <span
              v-else
              class="text--disabled font-italic"
            >
              Not available
            </span>
          </td>
          <td class="text-center">
            {{ props.item.issuer.company_name }}
          </td>
          <td class="text-center">
            {{ props.item.issuer.company_address }}
          </td>
          <td class="text-center">
            {{ props.item.issuer.register_id }}
          </td>
          <td class="text-center">
            {{ props.item.issuer.tax_identification }}
          </td>
          <td class="text-center">
            {{ props.item.issuer.tax_office }}
          </td>
          <td class="text-center">
            {{ props.item.issuer.ceo_name }}
          </td>
          <td class="text-center">
            <div v.if="props.item.issuer.iban">
              {{ props.item.issuer.iban }}
            </div>
            <div v.if="props.item.issuer.bic">
              {{ props.item.issuer.bic }}
            </div>
            <div v.if="props.item.issuer.bank_name">
              {{ props.item.issuer.bank_name }}
            </div>
          </td>
          <td class="text-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="secondary"
                  v-on="on"
                  @click.stop="() => onEdit(props.item)"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="error"
                  v-on="on"
                  @click.stop="() => showDeleteDialog(props.item.id)"
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </template>
              <span>Delete</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <CreateDialog
      v-if="isCreating"
      :is-open="isCreating"
      :security="currentSecurity"
      :toggle-open="onCreate"
    />

    <UploadCsvDialog
      :visible="uploadCSVDialog"
      @close="hideDialog()"
      @fileUpload="(file) => onUploadCsv(file)"
    />

    <DeleteDialog
      :visible="deleteDialog"
      :security-id="deleteDialogId"
      @close="hideDeleteDialog()"
      @submit="onDelete(deleteDialogId)"
    />

    <v-dialog
      v-model="csvUploadingDialog"
      persistent
      width="300"
    >
      <v-card
        color="primary"
        dark
      >
        <v-card-text>
          Please wait while uploading CSV.
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="csvResultDialog"
      width="800"
    >
      <v-card
        color="primary"
        dark
        class="pa-3"
      >
        <p class="font-weight-black">
          Uploading CSV finished - Result:
        </p>
        <p>Total in CSV: {{ csvUploadingResult.totalInCsv }}</p>
        <p>Total Valid: {{ csvUploadingResult.totalImported }}</p>
        <p>Total Saved to DB: {{ csvUploadingResult.totalDBInserts }}</p>
        <p>Total Failed: {{ csvUploadingResult.totalFailed }}</p>
        <p
          v-for="item in csvUploadingResult.failedReasons"
          :key="item.index"
          style="background-color: red;"
          class="pa-1"
        >
          {{ item.message }}
        </p>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CreateDialog from './dialogs/security/CreateDialog';
import DeleteDialog from './dialogs/security/DeleteDialog';
import UploadCsvDialog from './dialogs/security/UploadCsvDialog';

export default {
  name: 'Securities',
  components: {
    CreateDialog,
    DeleteDialog,
    UploadCsvDialog,
  },
  data() {
    return {
      csvUploadingDialog: false,
      csvUploadingResult: {
        totalDBInserts: 0,
        totalFailed: 0,
        totalImported: 0,
        totalInCsv: 0,
        failedReasons: [],
      },
      csvResultDialog: false,
      currentSecurity: {},
      isLoading: false,
      isCreating: false,
      isShowing: false,
      isEditing: false,
      uploadCSVDialog: false,
      deleteDialog: false,
      deleteDialogId: '',
      approvalDialogs: {},
      pagination: {
        rowsPerPage: 25,
      },
      search: '',
      headers: [
        // Security
        { textHeader: 'Security', text: 'ID', value: 'id', align: 'center' },
        { textHeader: 'Security', text: 'FpId', value: 'fp_id', align: 'center' },
        { textHeader: 'Security', text: 'Name', value: 'name', align: 'center' },
        { textHeader: 'Security', text: 'Type', value: 'type', align: 'center' },
        { textHeader: 'Security', text: 'Custodian', value: 'custodian', align: 'center' },
        { textHeader: 'Security', text: 'Notional', value: 'notional', align: 'center' },
        { textHeader: 'Security', text: 'Taxable In', value: 'taxable_in', align: 'center' },
        { textHeader: 'Security', text: 'Regulation Type', value: 'regulation_type', align: 'center' },
        // Issuer
        { textHeader: 'Issuer', text: 'Company ID', value: 'issuer.company_id', align: 'center' },
        { textHeader: 'Issuer', text: 'Company Name', value: 'issuer.company_name', align: 'center' },
        { textHeader: 'Issuer', text: 'Company Address', value: 'issuer.company_address', align: 'center' },
        { textHeader: 'Issuer', text: 'Register ID', value: 'issuer.register_id', align: 'center' },
        { textHeader: 'Issuer', text: 'Tax Identification', value: 'issuer.tax_identification', align: 'center' },
        { textHeader: 'Issuer', text: 'Tax Office', value: 'issuer.tax_office', align: 'center' },
        { textHeader: 'Issuer', text: 'CEO Name', value: 'issuer.ceo_name', align: 'center' },
        { textHeader: 'Issuer', text: 'IBAN + (BIC + Name)', value: 'issuer.iban', align: 'center' },
        // Actions
        { text: 'Actions', value: null, sortable: false, align: 'center' },
      ],
    };
  },
  computed: {
    securities() {
      return this.$store.getters.getSecuritiesList;
    },
    filteredItems() {
      const { search } = this;

      return search.length < 1 ?
        this.securities :
        this.securities.filter((item) => {
          let found = false;
          Object.keys(item)
            .map((key) => {
              if (item[key] && String(item[key]).toLowerCase().indexOf(search.toLowerCase()) > -1) {
                found = true;
              }

              return key;
            });

          return found;
        });
    },
  },
  methods: {
    types() {
      const data = this.$store.getters.getSecurityTypes;
      if (!data || !data.security || !data.security.types) {
        return {};
      }

      return data.security.types;
    },
    custodians() {
      const data = this.$store.getters.getSecurityTypes;
      if (!data || !data.security || !data.security.custodians) {
        return {};
      }

      return data.security.custodians;
    },
    onCreate() {
      this.isCreating = !this.isCreating;
      this.currentSecurity = {};
    },
    onEdit(item) {
      this.isCreating = !this.isCreating;
      this.currentSecurity = item;
    },
    onRefresh() {
      this.$store.dispatch('fetchPatSecurityAll');
    },
    showDialog() {
      this.uploadCSVDialog = true;
      this.$forceUpdate();
    },
    hideDialog() {
      this.uploadCSVDialog = false;
      this.$forceUpdate();
    },
    onUploadCsv(file) {
      this.csvUploadingDialog = true;
      this.$store
        .dispatch('callPatSecurityImportCSV', file)
        .then((e) => {
          this.csvUploadingDialog = false;
          this.csvUploadingResult = e;
          this.csvResultDialog = true;
          this.$store.dispatch('fetchPatSecurityAll');
        });
    },
    showDeleteDialog(id) {
      this.deleteDialogId = id;
      this.deleteDialog = true;
      this.$forceUpdate();
    },
    hideDeleteDialog() {
      this.deleteDialog = false;
      this.$forceUpdate();
    },
    onDelete(id) {
      this.$store
        .dispatch('deletePatSecurity', id)
        .then(() => {
          this.hideDeleteDialog();
        })
        .catch((err) => {
          this.error = err;
        });
    },
  },
};
</script>
<style lang="scss">
.bridge-signing-table {
  > .toolbar {
    position: relative;
    padding: 24px 24px 32px 24px;
    margin-bottom: 32px;
  }
}

.text-center {
  text-align: center;
}

.mass-create {
  margin-right: 70px;
}
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/Securities.vue